import { Injectable, inject } from '@angular/core'
import { AuthStorageService } from '@app/services/auth-storage/auth-storage.service'
import { addSeconds } from 'date-fns'
import { firstValueFrom } from 'rxjs'
import { AuthState, ClientCredentialsAuthResponse } from '@app/services/auth-storage/auth-storage.types'
import Bugsnag from '@bugsnag/js'
import { environment } from '@env/environment'
import { expectNonNil } from '@lib/assertions.lib'
import { errorMessage } from '@lib/common.lib'
import { Apollo } from 'apollo-angular'
import { HttpClient } from '@angular/common/http'

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly authStorageService = inject(AuthStorageService)
    private readonly apollo = inject(Apollo)
    private readonly http = inject(HttpClient)


    public async initialize(): Promise<void> {
        this.authStorageService.initialize()

        if (! this.authStorageService.isAuthenticated()) {
            await this.authorize()
        }
    }

    public async authorize(): Promise<void> {
        try {
            const { clientId, clientSecret, schema, hostname } = environment.api

            const response = await firstValueFrom(
                this.http.post<ClientCredentialsAuthResponse>(`${schema}://${hostname}/oauth/token`, {
                    grant_type: 'client_credentials',
                    client_id: clientId,
                    client_secret: clientSecret,
                }, {
                    responseType: 'json',
                    observe: 'body',
                }),
            )

            this.authStorageService.submitAuthState(
                this.createAuthState(response),
            )
        } catch (err: unknown) {
            Bugsnag.notify(`Failed to authorize client: ${errorMessage(err)}`)
            this.authStorageService.clearAuthState()
        }
    }

    /**
     * A last resort method to flush all authentication data (user and tokens) whenever
     * the application hits a state where authentication cannot be recovered. This is the
     * case when access- and/or refresh-tokens are revoked on the server, or can no longer
     * be found. In such cases we can not use the logout mutation, since that requires the
     * user to authorize with a valid access token too.
     */
    public async flushAuthData(): Promise<void> {
        await this.apollo.client.clearStore()
        this.authStorageService.clearAuthState()
    }

    private createAuthState(response: ClientCredentialsAuthResponse): AuthState {
        expectNonNil(response, 'Expected AuthPayload but got NIL.')

        return {
            accessToken: expectNonNil(
                response.access_token,
                'Invalid AuthPayload: missing access-token.',
            ),
            tokenType: expectNonNil(
                response.token_type,
                'Invalid AuthPayload: missing token-type.',
            ),

            expiresAt: addSeconds(
                new Date(),
                expectNonNil(
                    response.expires_in,
                    'Invalid AuthPayload: missing expires-in.',
                ),
            ),
        }
    }
}
